import { useEffect, useState } from "react";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Clock, Car, Check } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const [services, setServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_id: "",
    service_name: "",
    vehicle_type: "",
    vehicle_brand: "",
    notes: "",
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (!selectedDate) return;
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/available-times/${dateStr}`);
        setAvailableTimes(response.data.available_times);
        setSelectedTime("");
      } catch (error) {
        console.error("Error fetching available times:", error);
      }
    };
    fetchAvailableTimes();
  }, [selectedDate]);

  const handleServiceChange = (serviceId) => {
    const service = services.find((s) => s.id === serviceId);
    setFormData({
      ...formData,
      service_id: serviceId,
      service_name: service?.name || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error("Selecteer een datum en tijd");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        booking_date: format(selectedDate, "yyyy-MM-dd"),
        booking_time: selectedTime,
      });
      setSubmitted(true);
      toast.success("Boeking succesvol geplaatst!");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error("Er is iets misgegaan. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success" className="pt-20 min-h-screen bg-ug-black flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-black uppercase tracking-tighter text-white mb-4">
            Boeking Bevestigd!
          </h1>
          <p className="text-gray-400 mb-8">
            Bedankt voor uw boeking. We hebben een bevestiging gestuurd naar {formData.customer_email}.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4"
          >
            Nieuwe Boeking
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-ug-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-primary text-sm font-bold tracking-widest uppercase">
              Afspraak Maken
            </span>
            <h1 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tighter text-white mt-4">
              Boek Uw Detailing
            </h1>
            <p className="text-gray-400 text-lg mt-6">
              Kies een dienst, selecteer een datum en tijd, en wij zorgen voor de rest.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-ug-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Calendar Section */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-ug-paper border border-white/5 p-6">
                <div className="flex items-center mb-6">
                  <CalendarDays className="w-5 h-5 text-primary mr-3" />
                  <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                    Kies een Datum
                  </h3>
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={nl}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today || date.getDay() === 0;
                  }}
                  className="rounded-none border-0 bg-transparent [&_.rdp-day]:text-white [&_.rdp-day_button]:text-white [&_.rdp-day_button:hover]:bg-primary/20 [&_.rdp-button]:text-white"
                  data-testid="booking-calendar"
                />
              </div>

              {/* Time Selection */}
              {selectedDate && (
                <div className="bg-ug-paper border border-white/5 p-6" data-testid="time-selection">
                  <div className="flex items-center mb-6">
                    <Clock className="w-5 h-5 text-primary mr-3" />
                    <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                      Beschikbare Tijden
                    </h3>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    {format(selectedDate, "EEEE d MMMM yyyy", { locale: nl })}
                  </p>
                  {availableTimes.length === 0 ? (
                    <p className="text-gray-500">Geen tijden beschikbaar op deze datum</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableTimes.map((time) => (
                        <button
                          key={time}
                          type="button"
                          data-testid={`time-slot-${time}`}
                          onClick={() => setSelectedTime(time)}
                          className={`py-3 text-sm font-medium transition-colors duration-300 ${
                            selectedTime === time
                              ? "bg-primary text-white"
                              : "bg-white/5 text-gray-300 hover:bg-white/10"
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Form Section */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
                <div className="flex items-center mb-2">
                  <Car className="w-5 h-5 text-primary mr-3" />
                  <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                    Uw Gegevens
                  </h3>
                </div>

                {/* Service Selection */}
                <div className="space-y-2">
                  <Label htmlFor="service" className="text-white">Dienst *</Label>
                  <Select onValueChange={handleServiceChange} required>
                    <SelectTrigger
                      data-testid="service-select"
                      className="bg-ug-black border-white/10 text-white rounded-none h-12"
                    >
                      <SelectValue placeholder="Selecteer een dienst" />
                    </SelectTrigger>
                    <SelectContent className="bg-ug-black border-white/10">
                      {services.map((service) => (
                        <SelectItem
                          key={service.id}
                          value={service.id}
                          className="text-white hover:bg-white/10"
                        >
                          {service.name} - €{service.price.toFixed(0)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white">Naam *</Label>
                    <Input
                      id="name"
                      data-testid="booking-name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                      className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                      placeholder="Uw volledige naam"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white">Telefoonnummer *</Label>
                    <Input
                      id="phone"
                      data-testid="booking-phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      required
                      className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                      placeholder="06 12345678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">E-mailadres *</Label>
                  <Input
                    id="email"
                    data-testid="booking-email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    required
                    className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                    placeholder="uw@email.nl"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vehicle_type" className="text-white">Voertuig Type *</Label>
                    <Select
                      onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                      required
                    >
                      <SelectTrigger
                        data-testid="vehicle-type-select"
                        className="bg-ug-black border-white/10 text-white rounded-none h-12"
                      >
                        <SelectValue placeholder="Selecteer type" />
                      </SelectTrigger>
                      <SelectContent className="bg-ug-black border-white/10">
                        {["Sedan", "SUV", "Hatchback", "Stationwagen", "Coupé", "Cabrio", "Bus"].map(
                          (type) => (
                            <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                              {type}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle_brand" className="text-white">Merk & Model *</Label>
                    <Input
                      id="vehicle_brand"
                      data-testid="vehicle-brand"
                      value={formData.vehicle_brand}
                      onChange={(e) => setFormData({ ...formData, vehicle_brand: e.target.value })}
                      required
                      className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                      placeholder="bijv. BMW 3-serie"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-white">Opmerkingen</Label>
                  <Textarea
                    id="notes"
                    data-testid="booking-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-ug-black border-white/10 text-white rounded-none min-h-[100px] placeholder:text-gray-600"
                    placeholder="Bijzonderheden of specifieke wensen..."
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="submit-booking"
                  disabled={loading || !selectedDate || !selectedTime}
                  className="w-full bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold py-4 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Bezig met boeken..." : "Afspraak Bevestigen"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
